import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { LogOut, Plus, Home, User, ShieldCheck, Clock, ChevronDown, ListChecks, ArrowLeft } from 'lucide-react';
import VerifiedBadge from './VerifiedBadge';

const Header = ({ user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    onLogout();
  };

  const isRide24Home =
    location.pathname === '/ride-24' || location.pathname === '/ride-24/';

  const navLinkClass = (active) =>
    `px-3 py-2 rounded-md text-sm font-medium font-heading uppercase tracking-widest transition-colors ${
      active
        ? 'bg-red-600/15 text-red-400'
        : 'text-gray-400 hover:text-red-400 hover:bg-white/5'
    }`;

  const isOrganiser = user.is_verified || user.organiser_status === 'approved';
  const status = user.organiser_status;

  const roleChip = () => {
    if (isOrganiser) {
      return <VerifiedBadge size="sm" showLabel={false} />;
    }
    return null;
  };

  return (
    <header className="bg-black border-b border-gray-800 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4 md:space-x-8 min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <Link
                to="/dashboard"
                className="flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-sm font-heading uppercase tracking-widest text-gray-500 hover:text-red-400 transition-colors"
                data-testid="back-to-taurian-link"
              >
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Taurian
              </Link>
              <span className="h-4 sm:h-5 w-px bg-gray-800" aria-hidden="true" />
              <Link
                to="/ride-24"
                className="text-xl sm:text-2xl font-heading font-bold bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent hover:opacity-90 transition-opacity uppercase tracking-tight"
                data-testid="logo-link"
              >
                <i className="fa-solid fa-motorcycle text-red-500 mr-2 not-italic" aria-hidden="true"></i>
                Ride24
              </Link>
            </div>

            <nav className="hidden md:flex space-x-2">
              <Link to="/ride-24" className={navLinkClass(isRide24Home)} data-testid="dashboard-nav-link">
                <Home className="inline-block w-4 h-4 mr-1" />
                Dashboard
              </Link>
              <Link
                to="/ride-24/my-rides"
                className={navLinkClass(location.pathname.startsWith('/ride-24/my-rides'))}
                data-testid="my-rides-nav-link"
              >
                <ListChecks className="inline-block w-4 h-4 mr-1" />
                My Rides
              </Link>
              <Link
                to="/ride-24/create-event"
                className={navLinkClass(location.pathname.startsWith('/ride-24/create-event'))}
                data-testid="create-event-nav-link"
              >
                <Plus className="inline-block w-4 h-4 mr-1" />
                Create Ride
              </Link>
            </nav>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-white/5 transition-colors"
                data-testid="profile-menu-trigger"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full ring-2 ring-red-600/40"
                    data-testid="user-avatar"
                  />
                ) : (
                  <div className="w-8 h-8 bg-red-600/20 ring-2 ring-red-600/40 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-red-400" />
                  </div>
                )}
                <div className="hidden sm:flex flex-col text-left leading-tight">
                  <div className="text-sm font-medium text-gray-200 flex items-center gap-1" data-testid="user-name">
                    {user.name}
                    {roleChip()}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-heading">
                    {isOrganiser ? 'Verified organiser' : 'Rider'}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-zinc-400 hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-64 bg-zinc-950 border border-zinc-800 text-gray-200"
              data-testid="profile-menu-content"
            >
              <DropdownMenuLabel className="text-zinc-400 font-normal">
                <div className="text-white font-medium truncate">{user.name}</div>
                <div className="text-xs text-zinc-500 truncate">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-zinc-800" />

              {!isOrganiser && status === 'not_applied' && (
                <DropdownMenuItem
                  onClick={() => navigate('/ride-24/apply-organiser')}
                  className="cursor-pointer focus:bg-red-600/20 focus:text-red-300"
                  data-testid="menu-apply-organiser"
                >
                  <ShieldCheck className="w-4 h-4 mr-2 text-red-500" />
                  Be a verified Organiser
                </DropdownMenuItem>
              )}
              {!isOrganiser && status === 'pending' && (
                <DropdownMenuItem
                  onClick={() => navigate('/ride-24/apply-organiser')}
                  className="cursor-pointer focus:bg-yellow-600/20 focus:text-yellow-300"
                  data-testid="menu-application-pending"
                >
                  <Clock className="w-4 h-4 mr-2 text-yellow-500" />
                  Application pending
                </DropdownMenuItem>
              )}
              {!isOrganiser && status === 'rejected' && (
                <DropdownMenuItem
                  onClick={() => navigate('/ride-24/apply-organiser')}
                  className="cursor-pointer focus:bg-red-600/20 focus:text-red-300"
                  data-testid="menu-apply-again"
                >
                  <ShieldCheck className="w-4 h-4 mr-2 text-red-500" />
                  Apply again
                </DropdownMenuItem>
              )}
              {isOrganiser && (
                <DropdownMenuItem
                  disabled
                  className="text-red-400"
                  data-testid="menu-verified-status"
                >
                  <VerifiedBadge size="sm" /> <span className="ml-2">Verified organiser</span>
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-gray-300 focus:bg-white/5"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="md:hidden bg-black border-t border-gray-800">
        <nav className="flex space-x-1 px-4 py-2">
          <Link to="/ride-24" className={`flex-1 text-center ${navLinkClass(isRide24Home)}`} data-testid="mobile-dashboard-link">
            <Home className="w-5 h-5 mx-auto mb-1" />
            Dashboard
          </Link>
          <Link
            to="/ride-24/my-rides"
            className={`flex-1 text-center ${navLinkClass(location.pathname.startsWith('/ride-24/my-rides'))}`}
            data-testid="mobile-my-rides-link"
          >
            <ListChecks className="w-5 h-5 mx-auto mb-1" />
            My Rides
          </Link>
          <Link
            to="/ride-24/create-event"
            className={`flex-1 text-center ${navLinkClass(location.pathname.startsWith('/ride-24/create-event'))}`}
            data-testid="mobile-create-event-link"
          >
            <Plus className="w-5 h-5 mx-auto mb-1" />
            Create
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
